import { useMemo, useState } from "react";
import { analyzeCodons } from "@/lib/science/codon-analysis";
import { AMINO_ACIDS } from "@/lib/science/codon-table";
import { SciTextArea } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function RscuCalc() {
  const locale = getLocale();
  const [seq, setSeq] = useState("");
  const r = useMemo(() => analyzeCodons(seq), [seq]);

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="rscu-input"
        label={m.rscu_input_label()}
        value={seq}
        placeholder={m.rscu_input_placeholder()}
        onChange={setSeq}
      />

      {r.total > 0 ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.rscu_total_label()}
            </span>
            <span
              data-testid="rscu-total"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {r.total.toLocaleString(locale)}
            </span>
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-c)] text-left text-xs tracking-wider text-muted-foreground uppercase">
                  <th className="py-2 pr-4 font-semibold">
                    {m.rscu_col_codon()}
                  </th>
                  <th className="py-2 pr-4 font-semibold">{m.rscu_col_aa()}</th>
                  <th className="py-2 pr-4 font-semibold">
                    {m.rscu_col_count()}
                  </th>
                  <th className="py-2 font-semibold">{m.rscu_col_rscu()}</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {r.stats.map((s) => (
                  <tr
                    key={s.codon}
                    className="border-b border-[var(--border-c)]/50"
                  >
                    <td className="py-2 pr-4 font-semibold text-foreground">
                      {s.codon}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {s.aa === "*"
                        ? "Stop"
                        : `${s.aa} (${AMINO_ACIDS[s.aa]?.three})`}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {s.count.toLocaleString(locale)}
                    </td>
                    <td
                      className={
                        s.rscu > 1.05
                          ? "sci-result py-2 font-semibold"
                          : s.rscu < 0.95
                            ? "py-2 text-muted-foreground"
                            : "py-2 text-foreground"
                      }
                    >
                      {s.rscu.toLocaleString(locale, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.rscu_hint_empty()}
        </p>
      )}
    </div>
  );
}
